import { useState, useEffect, useRef, useMemo } from "react";
import { useParams, useLoaderData, Link, useSearchParams } from "react-router-dom";

import { Server, Code, ExternalLink, File, FileText, Globe, Lock, Package, Sliders, Terminal, Tool, User, AlertTriangle, Key, Cpu, Info, ArrowRight, ChevronRight, ChevronDown, Plus, X, RefreshCcw } from "react-feather";
import { ReactComponent as MCPLogo } from '../../svg/mcp.svg';
import {
  DetailedPackageDeploymentSchema,
  DetailedPackageFileSchema,
  PackageEndpointSchema,
  PackageFileSchema,
  PackageSchema,
  ApiKeychainSchema
} from "../../utils/types";
import PackageCard from "./PackageCard";

import API, { ErrorWithDetails } from "../../utils/api";
import { PackageDetailsPageLoaderData } from "./PackageDetailsPageLoader";
import { InternalLink } from "../../components/internal-link/InternalLink";
import "./PackageDetailsPage.scss";
import { MiniEditor } from "../../components/editor/MiniEditor";
import { Helmet } from "react-helmet-async";
import { Breadcrumb, useBreadcrumbs } from "../../context/BreadcrumbsContext";
import { AlertModal } from "../../components/modals/AlertModal";
import { ContentButton } from "../../components/button/ContentButton";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { toast } from "../../services/toast";
import Textbox from "../../components/textbox/Textbox";
import Select from "../../components/select/Select";
import { InfoBox } from "../../components/info/InfoBox";
import { formatDate } from "../../utils/time";

  // returns a search param string, uri encoded
const serializeJSONArguments = (jsonArguments: { [key: string]: any }) => {
  return Object.entries(jsonArguments).map(([key, value]) => `${key}=${encodeURIComponent(value)}`).join('&');
};

const EXAMPLE_CODE_CURL = (method: string, url: string, jsonArguments: { [key: string]: any }) => {
  if (method === 'GET' || method === 'DELETE') {
    const searchParams = serializeJSONArguments(jsonArguments);
    return `
curl -X ${method} '${url}'${searchParams ? ` \\\n  '?${searchParams}'` : ''} \\
  -H 'Authorization: Bearer $KEYCHAIN_SECRET_KEY'
`.trim();
  } else {
    return `
curl -X ${method} '${url}' \\
  -H 'Authorization: Bearer $KEYCHAIN_SECRET_KEY' \\
  -H 'Content-Type: application/json' \\
  -d '${JSON.stringify(jsonArguments)}'
`.trim();
  }
};

const EXAMPLE_CODE_NODEJS = (method: string, url: string, jsonArguments: { [key: string]: any }) => {
  const stringifiedArguments = JSON.stringify(jsonArguments, null, 2)
    .split('\n')
    .map((line, i) => i === 0 ? line : `  ${line}`)
    .join('\n')
    .replaceAll('"', '\'');
  if (method === 'GET' || method === 'DELETE') {
    return `
import io from 'io';
const KEYCHAIN_SECRET_KEY = process.env.KEYCHAIN_SECRET_KEY;

const result = await io.request(
  '${method}',
  '${url}',
  ${stringifiedArguments},
  {
    'Authorization': \`Bearer \${KEYCHAIN_SECRET_KEY}\`,
  }
);`.trim();
  } else {
    return `
import io from 'io';
const KEYCHAIN_SECRET_KEY = process.env.KEYCHAIN_SECRET_KEY;

const result = await io.request(
  '${method}',
  '${url}',
  {}, // query params empty
  {
    'Authorization': \`Bearer \${KEYCHAIN_SECRET_KEY}\`,
    'Content-Type': 'application/json'
  },
  JSON.stringify(${stringifiedArguments})
);`.trim();
  }
};

export function PackageEndpoint({
  endpoint,
  link = false
}: {
  endpoint: PackageEndpointSchema,
  link?: boolean
}) {

  return (
    <div data-component="PackageEndpoint">
      <div className="package-endpoint-header">
        <div className="package-endpoint-header-icon">
          <Tool />
        </div>
        <div className="package-endpoint-name">
          <div className="package-endpoint-title">
            {link && (
              <InternalLink to={`/packages/${endpoint.function_info.package.name}/${endpoint.function_info.package.version}/${endpoint.function_info.filename}?method=${endpoint.function_info.method}`}>
                {endpoint.function_info.summary.title}
              </InternalLink>
            )}
            {!link && (
              <span>{endpoint.function_info.summary.title}</span>
            )}
          </div>
          {endpoint.function_info.summary.title !== `${endpoint.function_info.method} ${endpoint.function_info.route}` && (
            <div className="package-endpoint-route">
              <span className="package-endpoint-method">
                <span className={endpoint.function_info.method}>{endpoint.function_info.method}</span>
              </span>
              <span className="route-name">
                {endpoint.function_info.route}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function PackageEndpointDetails({
  endpoint,
}: {
  endpoint: PackageEndpointSchema
}) {

  const toolArguments = useMemo(() => {
    const props = endpoint.function_info.parameters.properties;
    const lines = [];
    for (const [ key, value ] of Object.entries(props)) {
      const v = value as any;
      let type = v.type || 'any';
      if (type === 'array') {
        type = `list of ${v.items.type}s`;
      }
      const output = (
        <div className="tool-argument" key={key}>
          <div className="tool-argument-row">
            <div className="tool-argument-name">{key}</div>
            <div className="tool-argument-type">&nbsp;({type})</div>
            {!v.hasOwnProperty('default') && (
              <>
                <span className="middot">&middot;</span>
                <div className="tool-argument-required">required</div>
              </>
            )}
            {v.hasOwnProperty('default') && (
              <>
                <span className="middot">&middot;</span>
                <div className="tool-argument-default">
                  optional, default <code>{JSON.stringify(v.default)}</code>
                </div>
              </>
            )}
          </div>
          {v.description && (
            <div className="tool-argument-row sub">
              <div className="tool-argument-description">{v.description}</div>
            </div>
          )}
          {(
            v.hasOwnProperty('minimum') ||
            v.hasOwnProperty('maximum')
          ) && (
            <div className="tool-argument-row sub">
              {v.minimum && (
                <>
                  <span className="middot">&middot;</span>
                  <div className="tool-argument-default">
                    min <code>{JSON.stringify(v.minimum)}</code>
                  </div>
                </>
              )}
              {v.maximum && (
                <>
                  <span className="middot">&middot;</span>
                  <div className="tool-argument-default">
                    max <code>{JSON.stringify(v.maximum)}</code>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      );
      lines.push(output);
    }
    return lines;
  }, [endpoint.function_info.parameters.properties]);

  const toolDescription = useMemo(() => {
    const description = [
      endpoint.function_info.summary.title,
      endpoint.function_info.summary.subtitle
    ].join('\n').trim();
    const lines = description.length > 0 ? description.split('\n') : [];
    const output = [];
    for (const line of lines) {
      output.push(<div className="tool-description-line" key={line}>{line}</div>);
    }
    return output;
  }, [endpoint.function_info.summary.title, endpoint.function_info.summary.subtitle]);

  return (
    <div data-component="PackageEndpointDetails">
      <div className="package-single-endpoint">
        <div className={`description ${!toolDescription.length ? 'empty' : ''}`}>
          {toolDescription.length > 0 ? toolDescription : 'No description available'}
        </div>
      </div>
      <div className="package-endpoints-header">
        <Terminal />
        <span>Arguments</span>
        <span className="label middot">&middot;</span>
        <span className="label">Requests to this tool can provide the following arguments</span>
      </div>
      <div className="package-single-endpoint">
        <div className={`parameters ${!toolArguments.length ? 'empty' : ''}`}>
          {toolArguments.length > 0 ? toolArguments : 'No arguments required'}
        </div>
      </div>
    </div>
  );

};

export function PackageEndpointDeveloperNotes({
  endpoint,
}: {
  endpoint: PackageEndpointSchema
}) {

  const functionArguments = useMemo(() => {
    const params = { ...endpoint.function_info.parameters };
    const props = params.properties;
    const required = Object.keys(props).filter(key => !props[key].hasOwnProperty('default'));
    return JSON.stringify({ ...params, required }, null, 2);
  }, [endpoint.function_info.parameters]);

  const functionInterface = useMemo(() => {
    const props = endpoint.function_info.parameters.properties;
    const lines = [`interface FunctionArguments {`];
    for (const [ key, value ] of Object.entries(props)) {
      const v = value as any;
      let type = v.type || 'any';
      if (type === 'array') {
        type = `${v.items.type}[]`;
      }
      if (v.description) {
        lines.push(`  /** ${v.description} */`);
      }
      lines.push(`  ${key}${v.default ? '?' : ''}: ${type};${v.default ? ` // = ${JSON.stringify(v.default)}` : ''}`);
    }
    if (lines.length === 1) {
      lines.push('  // No parameters');
    }
    lines.push('}');
    return lines.join('\n');
  }, [endpoint.function_info.parameters.properties]);

  const JSONArguments = useMemo(() => {
    const params = { ...endpoint.function_info.parameters };
    const props = params.properties;
    const json: { [key: string]: any } = {};
    for (const [ key, value ] of Object.entries(props)) {
      const v = value as any;
      json[key] = v.default;
      if (json[key] === void 0) {
        if (v.type === 'string') {
          json[key] = 'some string';
        } else if (v.type === 'number') {
          json[key] = 0;
        } else if (v.type === 'boolean') {
          json[key] = true;
        } else if (v.type === 'array') {
          json[key] = [];
        } else if (v.type === 'object') {
          json[key] = {};
        } else {
          json[key] = null;
        }
      }
    }
    return json;
  }, [endpoint.function_info.parameters]);

  const codeExampleOptions = [
    {
      name: 'cURL',
      language: 'bash',
      value: EXAMPLE_CODE_CURL(endpoint.function_info.method, endpoint.function_info.url, JSONArguments)
    },
    {
      name: 'Node.js',
      language: 'javascript',
      value: EXAMPLE_CODE_NODEJS(endpoint.function_info.method, endpoint.function_info.url, JSONArguments)
    }
  ];
  const [ codeExampleOption, setCodeExampleOption ] = useState(codeExampleOptions[0].name);

  const [ parametersOptionName, setParametersOptionName ] = useState('Typescript');

  return (
    <div data-component="PackageEndpointDeveloperNotes">
      <h1>
        <Code />
        <span>Developer notes</span>
      </h1>
      <div className="code-example">
        <MiniEditor
          display="package"
          label="Usage example"
          copyable={true}
          options={codeExampleOptions}
          selectedOptionName={codeExampleOption}
          onSelectOption={(option) => {
            setCodeExampleOption(option.name);
          }}
          readonly
        />
      </div>
      <div className="code-example">
        <MiniEditor
          display="package"
          label="Request parameters"
          copyable={true}
          language="typescript"
          value={functionInterface}
          selectedOptionName={parametersOptionName}
          options={[
            {
              name: 'TypeScript',
              language: 'typescript',
              value: functionInterface,
            },
            {
              name: 'JSON Schema',
              language: 'json',
              value: functionArguments,
            }
          ]}
          onSelectOption={(option) => setParametersOptionName(option.name)}
          readonly={true}
        />
      </div>
    </div>
  );

};

export default function PackageDetailsPage() {

  const {
    packageName,
    version,
    environment,
    pkg,
    packageVersion,
    packageDeployment,
    filename,
    file,
    fileEndpoint,
    methods
  } = useLoaderData() as PackageDetailsPageLoaderData;

  const [ searchParams, setSearchParams ] = useSearchParams();
  const method = searchParams.get('method');

  // MCP link instructions
  const [ apiKeychains, setApiKeychains ] = useState<ApiKeychainSchema[]>([]);
  const [ selectedApiKeychain, setSelectedApiKeychain ] = useState<ApiKeychainSchema | null>(null);

  const [ isInstallingToApiKeychain, setIsInstallingToApiKeychain ] = useState(false);
  const [ activeApiKeychain, setActiveApiKeychain ] = useState<ApiKeychainSchema | null>(null);

  const { user, organization } = useAuth();
  const { updateBreadcrumbs } = useBreadcrumbs();
  const navigate = useNavigate();

  const [ viewPkg, setViewPkg ] = useState<PackageSchema>(pkg);

  const requiredKeys = useMemo(() => {
    return packageDeployment?.required_keys || [];
  }, [packageDeployment]);

  let title = viewPkg.agent
    ? viewPkg.agent.agentConfigs?.[0]?.name.split('/').slice(-1)[0]
    : viewPkg.display_title;
  let displayTitle = title;
  let packageTitle = displayTitle;
  if (fileEndpoint) {
    title = `${fileEndpoint.function_info.summary.title} | ${title} | Endpoint`;
    displayTitle = fileEndpoint.function_info.summary.title;
  } else if (filename) {
    title = `${filename.split('/').slice(-1)[0]} | ${title} | File`;
    displayTitle = filename.split('/').slice(-1)[0];
  } else {
    title = `${title}${version || environment ? ` (${version || environment})` : ''} | Package`;
  }
  const description = pkg?.description || 'View this package';
  let rootPath = window.location.pathname;
  if (filename &&rootPath.endsWith(`/${filename}`)) {
    rootPath = rootPath.slice(0, -(filename.length + 1));
  }
  if (rootPath.endsWith(version)) {
    rootPath = rootPath.slice(0, -(version.length + 1));
  }
  if (rootPath.endsWith(environment)) {
    rootPath = rootPath.slice(0, -(environment.length + 1));
  }

  const installToApiKeychain = async (apiKeychain: ApiKeychainSchema | null) => {
    if (!user) {
      toast.message({ type: 'warning', message: 'You must be logged in to install to an API keychain', duration: 3000 });
      navigate('/signup');
      return;
    }
    setIsInstallingToApiKeychain(true);
    try {
      let createdApiKeychain = false;
      let activeApiKeychain = null;
      if (!apiKeychain) {
        const result = await API.post('v1/api_keychains', {
          organization: organization.name,
          alias: `API keychain for ${viewPkg.display_name} on ${formatDate(new Date())}`,
          usage_type: 'package'
        });
        activeApiKeychain = result as ApiKeychainSchema;
        createdApiKeychain = true;
      } else {
        const result = await API.get(`v1/api_keychains/details`, {
          id: apiKeychain.unique_id
        });
        activeApiKeychain = result as ApiKeychainSchema;
      }
      const availablePackages = activeApiKeychain.keychainConfigs?.[0]?.available_packages || {};
      const activePackage = availablePackages[viewPkg.display_name];
      const version = packageVersion?.version || packageVersion?.environment;
      if (activePackage && activePackage.version === version) {
        setActiveApiKeychain(activeApiKeychain);
      } else {
        if (activePackage && activePackage.version !== version) {
          const result = window.confirm(
            `This API keychain already has an active installation of this package: "${activePackage.version}".\n` +
            `Would you like to replace it with "${version}"?`
          );
          if (!result) {
            throw new Error('Installation aborted');
          }
        }
        const pkg = { ...(activePackage || {}) };
        pkg.version = version;
        if (!pkg.hasOwnProperty('sha256')) {
          pkg.sha256 = packageDeployment?.code_sha256;
        }
        if (!pkg.hasOwnProperty('permissions')) {
          pkg.permissions = ['*'];
        }
        if (!pkg.hasOwnProperty('keys')) {
          pkg.keys = packageDeployment?.required_keys.map(key => key.name);
        }
        const updatePackages = { ...availablePackages, [viewPkg.display_name]: pkg };
        const updateResult = await API.put('v1/api_keychains', {
          id: activeApiKeychain.unique_id,
          packages: updatePackages
        });
        activeApiKeychain = updateResult as ApiKeychainSchema;
        setActiveApiKeychain(activeApiKeychain);
      }
      toast.message({ type: 'success', message: `Package installed to API keychain`, duration: 3000 });
      setIsInstallingToApiKeychain(false);
      if (createdApiKeychain) {
        setSelectedApiKeychain(activeApiKeychain);
        setApiKeychains([ activeApiKeychain, ...apiKeychains ]);
      }
    } catch (e) {
      const err = e as Error;
      toast.message({ type: 'error', message: `Failed to install to API keychain:\n${err.message}`, duration: 3000 });
      setIsInstallingToApiKeychain(false);
    }
  };

  useEffect(() => {
    if (filename) {
      updateBreadcrumbs([
        {
          icon: User,
          label: `@${viewPkg.organization.name}`,
          path: `/packages/@${viewPkg.organization.name}`
        },
        {
          icon: viewPkg.display_name.startsWith('@') ? Package : viewPkg.agent ? Cpu : Lock,
          label: packageTitle,
          path: rootPath
        },
        {
          icon: viewPkg.display_name.startsWith('@') ? Package : viewPkg.agent ? Cpu : Lock,
          label: `${packageTitle} (${version || environment})`,
          path: `${rootPath}/${version || environment}`
        },
        {
          icon: fileEndpoint ? Tool : File,
          label: displayTitle,
          path: `${rootPath}/${version || environment}/${filename}` + (method ? `?method=${method}` : '')
        }
      ]);
    } else {
      const breadcrumbs: Breadcrumb[] = [
        {
          icon: User,
          label: `@${viewPkg.organization.name}`,
          path: `/packages/@${viewPkg.organization.name}`
        },
        {
          icon: viewPkg.display_name.startsWith('@') ? Package : viewPkg.agent ? Cpu : Lock,
          label: packageTitle,
          path: rootPath
        }
      ];
      if (version || environment) {
        breadcrumbs.push({
          icon: viewPkg.display_name.startsWith('@') ? Package : viewPkg.agent ? Cpu : Lock,
          label: `${packageTitle} (${version || environment})`,
          path: `${rootPath}/${version || environment}`
        });
      }
      updateBreadcrumbs(breadcrumbs);
    }
    return () => {
      updateBreadcrumbs([]);
    };
  }, [rootPath, fileEndpoint, filename, packageTitle, displayTitle, viewPkg, version, environment]);

  useEffect(() => {
    if (user) {
      (async () => {
        try {
          const result = await API.get('v1/api_keychains', {
            organization: organization.name,
            usage_type: 'standalone'
          });
          const apiKeychains = (result?.data || []) as ApiKeychainSchema[];
          setApiKeychains(apiKeychains);
        } catch (e) {
          console.error(e);
        }
      })();
    }
  }, [user, organization]);

  const apiKeychainOptions = useMemo(() => {
    const options: { label: string; disabled?: boolean; value: string | null | undefined }[] = [
      {
        label: 'Create a new API keychain',
        value: null
      }
    ];
    if (apiKeychains.length > 0) {
      options.push({
        label: '─',
        disabled: true,
        value: void 0
      });
    }
    options.push(...apiKeychains.map(apiKeychain => {
      return {
        label: apiKeychain.alias,
        value: apiKeychain.unique_id
      };
    }));
    return options;
  }, [apiKeychains]);

  const packageFilesDisplay = useMemo(() => {
    return packageDeployment?.packageFiles
      .sort((fa, fb) => {
        const da = fa.filename.indexOf('/') > -1;
        const db = fb.filename.indexOf('/') > -1;
        return da === db
          ? fa.filename === fb.filename
            ? 0
            : fa.filename > fb.filename
              ? 1
              : -1
          : da > db
            ? -1
            : 1;
      })
      .map(curFile => {
        return (
          <tr
            key={curFile.filename}
            className={curFile.filename === file?.filename ? 'selected' : ''}
            >
            <td className="icon">
              <File />
            </td>
            <td>
              <InternalLink to={`/packages/${packageName}/${packageVersion?.version || packageVersion?.environment}/${curFile.filename}`}>
                {curFile.filename.replace(/\//g, '/\u200B')}
              </InternalLink>
            </td>
            {false && <td className="content-type">
              {curFile.content_type}
            </td>}
            <td className="size">
              {curFile.size} B
            </td>
          </tr>
        );
      });
  }, [packageDeployment?.packageFiles]);

  const [isArchiving, setIsArchiving] = useState(false);
  const [showArchiveAlert, setShowArchiveAlert] = useState(false);

  const archivePackage = async () => {
    setIsArchiving(true);
    try {
      await API.del('v1/packages', { 
        name: pkg?.display_name,
        environment: packageVersion?.environment || environment,
        version: packageVersion?.version || null
      });
      toast.message({ type: 'success', message: `Environment archived`, duration: 1000 });
      setIsArchiving(false);
      setShowArchiveAlert(false);
      // Redirect to package list after archive
      if (viewPkg.packageVersions.length > 1) {
        const packageVersions = viewPkg.packageVersions
          .filter(v => v.environment !== packageVersion?.environment && v.version !== packageVersion?.version);
        navigate(`/packages/${viewPkg.display_name}/${packageVersions[0].version || packageVersions[0].environment}`);
      } else {
        navigate(`/packages/@${viewPkg.organization.name}`);
      }
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to archive environment';
      toast.message({ type: 'error', message, duration: 5000 });
    }
  };

  return (
    <div data-component="PackageDetailsPage">
      <Helmet>
        <title>{title} | Instant.bot</title>
        <meta name="description" content={description} />
      </Helmet>
      {pkg && (
        <div className="packages-list">
          <PackageCard
            key={viewPkg.display_name}
            pkg={pkg}
            environment={environment}
            version={version}
            mode="page"
            onEdit={(pkg) => setViewPkg(pkg)}
          />
          {packageVersion && packageDeployment && (
            <div className="package-details">
              {!filename && (
                <>
                  <div className="package-endpoints-header">
                    <span>Endpoints</span>
                    <span className="label middot">&middot;</span>
                    <span className="label">All endpoints are available as tools for your agent</span>
                  </div>
                  <div className="package-endpoints">
                    {packageDeployment.packageEndpoints.map(endpoint => {
                      return (
                        <PackageEndpoint
                          key={endpoint.function_info.name}
                          endpoint={endpoint}
                          link={true}
                        />
                      )
                    })}
                  </div>
                </>
              )}
              {!filename && (
                <>
                  {requiredKeys.length > 0 && (
                    <>
                      <div className="package-files-header">
                        <span>Required keys</span>
                        <span className="label middot">&middot;</span>
                        <span className="label">Your keychain must contain the following keys to use this package</span>
                      </div>
                      <div className="package-keychain-keys">
                        {requiredKeys.map((key, index) => (
                          <div className="package-keychain-key" key={index}>
                            <Key />
                            <div className="key-entry">
                              <span className="key-name">{key.name}</span>
                              <span className="key-description">{key.description}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                  <div className="package-endpoints-header">
                    <span>Servers</span>
                    <span className="label middot">&middot;</span>
                    <span className="label">Web URLs to integrate this package with other services</span>
                  </div>
                  <div className="package-endpoints">
                    <div className="package-server-url">
                      <Server />
                      <span>
                        <span>
                          <strong>REST API Server</strong>
                          <span>Make HTTP requests from any codebase</span>
                        </span>
                        <InternalLink to={viewPkg.version_urls[packageVersion.version || packageVersion.environment]} target="_blank">
                          {viewPkg.version_urls[packageVersion.version || packageVersion.environment]}
                          <ExternalLink />
                        </InternalLink>
                      </span>
                    </div>
                    <div className="package-server-url">
                      <MCPLogo />
                      <span>
                        <span>
                          <strong>MCP Server</strong>
                          <span>OpenAI, Anthropic, Cursor and other MCP clients</span>
                        </span>
                        <InternalLink to={viewPkg.version_urls[packageVersion.version || packageVersion.environment] + '/server.mcp'} target="_blank">
                          {viewPkg.version_urls[packageVersion.version || packageVersion.environment]}/server.mcp
                          <ExternalLink />
                        </InternalLink>
                      </span>                      
                    </div>
                    <div className="package-server-url-details">
                      <div className="package-server-url-details-header">
                        <strong>URL</strong>
                      </div>
                      <Textbox
                        size="small"
                        monospace
                        copyable
                        readonly={true}
                        value={viewPkg.version_urls[packageVersion.version || packageVersion.environment] + '/server.mcp'}
                      />
                      <div className="package-server-url-details-header">
                        <strong>Label</strong>
                      </div>
                      <Textbox
                        size="small"
                        monospace
                        copyable
                        readonly={true}
                        value={viewPkg.display_title}
                      />
                      <div className="package-server-url-details-header">
                        <strong>Access token</strong>
                        <span>
                          You must install this package to an API keychain to generate an access token
                        </span>
                      </div>
                      {!activeApiKeychain && (
                        <>
                          <div className="input-container">
                            <Select
                              size="small"
                              options={apiKeychainOptions}
                              value={selectedApiKeychain?.unique_id || null}
                              onChange={(value) => {
                                if (value) {
                                  const apiKeychain = apiKeychains.find(apiKeychain => apiKeychain.unique_id === value);
                                  setSelectedApiKeychain(apiKeychain || null);
                                } else {
                                  setSelectedApiKeychain(null);
                                }
                              }}
                            />
                            <ContentButton
                              icon={Plus}
                              color={selectedApiKeychain ? 'green' : 'blue'}
                              onClick={() => installToApiKeychain(selectedApiKeychain)}
                              loading={isInstallingToApiKeychain}
                            >
                              {selectedApiKeychain ? 'Install to API keychain' : 'Create API keychain'}
                            </ContentButton>
                          </div>
                        </>
                      )}
                      {activeApiKeychain && (
                        <div className="input-container">
                          <Textbox
                            size="small"
                            monospace
                            copyable
                            readonly={true}
                            value={activeApiKeychain.secret_key || ''}
                          />
                          <ContentButton
                            icon={RefreshCcw}
                            color="grey"
                            onClick={() => {
                              setActiveApiKeychain(null);
                            }}
                          >
                            Change
                          </ContentButton>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="package-files-header">
                    <span>Codebase</span>
                    <span className="label middot">&middot;</span>
                    <span className="label">Public packages are open source, while agent and private packages are not</span>
                  </div>
                  <div className="package-files">
                    <table>
                      <tbody>
                        {!packageDeployment.packageFiles.length && (
                          <tr>
                            <td className="package-files-empty">
                              This type of package is not open source.
                            </td>
                          </tr>
                        )}
                        {packageFilesDisplay}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

              {fileEndpoint && (
                <>
                  <div className="package-endpoints-header">
                    <Tool />
                    <span>{fileEndpoint.function_info.route}</span>
                    <span className="label middot">&middot;</span>
                    {
                      methods.map((method, index) => {
                        const selected = method === fileEndpoint.function_info.method;
                        return (
                          <span className="container" key={method}>
                            {selected && (
                              <span>{method}</span>
                            )}
                            {!selected && (
                              <span className="label">
                                <InternalLink to={`/packages/${packageName}/${packageVersion?.version || packageVersion?.environment}/${fileEndpoint.function_info.filename}?method=${method}`}>
                                  {method}
                                </InternalLink>
                              </span>
                            )}
                            {index !== methods.length - 1 && <span className="label">&middot;</span>}
                          </span>
                        )
                      })
                    }
                  </div>
                  <PackageEndpointDetails endpoint={fileEndpoint} />
                </>
              )}

              {file && (
                <div className="package-single-file">
                  {!fileEndpoint && (
                    <div className="package-single-file-header">
                      <File />
                      <span>{file.filename}</span>
                    </div>
                  )}
                  {fileEndpoint && (
                    <div className="package-single-file-header">
                      <File />
                      <span>{file.filename}</span>
                      <span className="label middot">&middot;</span>
                      <span className="label">Endpoint file</span>
                    </div>
                  )}
                  <div className="package-file-details">
                    <MiniEditor
                      language="javascript"
                      value={file.text_content || ''}
                      readonly={true}
                    />
                  </div>
                </div>
              )}

              {fileEndpoint && (
                <PackageEndpointDeveloperNotes endpoint={fileEndpoint} />
              )}

              {!filename && packageVersion && (organization?.name === viewPkg.organization.name) && (
                <>
                  <div className="package-danger-header">
                    <AlertTriangle />
                    <span>Danger zone</span>
                    <span className="label middot">&middot;</span>
                    <span className="label">As package administrator, you can take destructive actions</span>
                  </div>
                  <div className="package-danger-zone">
                    <div className="danger-action">
                      <div className="danger-action-description">
                        <div className="title">
                          Archive environment or version
                        </div>
                        <div className="description">
                          Package version will no longer be available for use with agents or via the API.
                        </div>
                      </div>
                      <ContentButton
                        color="red"
                        onClick={() => setShowArchiveAlert(true)}
                        loading={isArchiving}
                      >
                        Archive {packageVersion?.version || packageVersion?.environment}
                      </ContentButton>
                    </div>
                  </div>
                  {showArchiveAlert && (
                    <AlertModal
                      message={`Archive the ${packageVersion?.version || packageVersion?.environment || version} version of this package?\nThis action cannot be undone.`}
                      onClose={() => setShowArchiveAlert(false)}
                      onConfirm={async (complete) => {
                        await archivePackage();
                        complete();
                      }}
                      onCancel={() => {}}
                    />
                  )}
                </>
              )}

            </div>
          )}
        </div>
      )}
    </div>
  );

};